<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="call-widget-container">
    <div class="call-widget-avatar">
      <div v-for="participant in participants" :key="participant.id">
        <EcsAvatar
          class="conference-avatar"
          :size="hasEnded ? avatarSize - 20 : avatarSize"
          :agent-id="isAgent(participant) ? participant.id : null"
          :contact-id="isAgent(participant) ? null : participant.id"
          :alt="participant.name"
          no-animation
        />
      </div>

      <EcsDialpad
        show-only-digits
        class="ecs-telephony-element-dialpad"
        :show="isDialpadVisible"
        :disabled="isOfflineModeEnabled"
        centered
        close-on-overlay-click
        @digit-pressed="handleDialpadDigitPressed"
        @dialpad-closed="handleDialpadClosed"
      />
    </div>

    <div v-show="hasEnded" class="call-widget-message" :dark="$vuetify.theme.dark">
      <v-icon small>mdi-phone-hangup</v-icon>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-if="interaction.isCallback" class="message" v-html="$t('callHasEnded', { endedTimeAgo })" />
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-else-if="!calculatedDuration" class="message" v-html="$t('callHasEnded', { endedTimeAgo })" />
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-else
        class="message"
        v-html="$t('endedWithDuration', { endedTimeAgo, talkTime, waitTime, handlingDuration })"
      />
    </div>

    <div v-show="!hasEnded" class="call-widget elevation-8">
      <div v-show="element.isOnHold" class="on-hold-counter">{{ onHoldFormatted }} {{ $t('onHold') }}</div>
      <div class="call-widget-controls">
        <div class="call-widget-timer">
          {{ callDurationFormatted }}
        </div>

        <v-divider class="mx-4" vertical />

        <v-tooltip v-for="(item, index) in options" :key="index" top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-show="!item.hidden"
              class="control"
              :color="item.active ? 'primary' : ''"
              :disabled="isOfflineModeEnabled || item.disabled || interaction.isHistoric"
              :loading="isWaitingFor(item.waitsFor)"
              icon
              v-on="on"
              @click="item.action()"
            >
              <v-icon>{{ item.icon }}</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(item.label) }}</span>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { ELEMENT_STATE } from '@/constants/element/constants';
import EcsDialpad from '@/components/ecs-dialpad/EcsDialpad';
import EcsAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsAvatar';
import InteractionService from '@/services/interactionService';
import { CONTACT_TYPE } from '@/constants/contactConstants';
import EcsKpiFormatter from '@/mixins/ecs-kpi-formatter/EcsKpiFormatter';
import EcsSoftPhoneMixin from '@/components/ecs-soft-phone/EcsSoftPhoneMixin';
import { ACTIONS } from '@/constants/softPhone/constants';
import EcsInteraction from '@/components/ecs-interaction/EcsInteraction';

export default {
  name: 'EcsCallWidget',

  components: { EcsAvatar, EcsDialpad },

  mixins: [EcsSoftPhoneMixin],

  props: {
    elementId: {
      type: String,
      default: ''
    },
    interaction: {
      type: Object,
      required: true,
      default: () => {}
    }
  },

  data() {
    return {
      callDurationTimer: null,
      callDuration: 0, // Milliseconds
      avatarSize: 150,
      isDialpadVisible: false,
      undefinedDateTime: '0001-01-01T00:00:00.000+00:00',
      isMuted: false,
      directCallInteractionIdOnHold: '',
      onHoldSince: null,
      onHoldFormatted: '00:00:00',
      onHoldCounterInterval: null
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('ELEMENTS', ['getElement', 'getElementsForInteraction']),

    ...mapGetters('EVENT_TIMING', ['isWaitingForEvent']),

    ...mapGetters('SETTINGS', ['isOfflineModeEnabled']),

    ...mapGetters('INTERACTIONS/recordings', ['getCallRecordingStateByInteractionId']),

    element() {
      let baseElement = this.getElement(this.elementId);

      if (!baseElement) {
        const elements = this.getElementsForInteraction(this.interaction);
        if (elements.length) {
          baseElement = elements[0];
        }
      }

      return baseElement || {};
    },

    participants() {
      if (this.interaction.isDirectCall) {
        return [
          {
            id: this.interaction.crm.contactId,
            name: this.interaction.customerName,
            partyType: EcsInteraction.partyType.customer
          }
        ];
      }

      if (this.interaction.isCampaign) {
        return [
          {
            id: this.interaction.routing.campaignId,
            name: this.interaction.routing.campaignName,
            partyType: EcsInteraction.partyType.customer
          }
        ];
      }

      return this.element.receptors
        .filter(receptor => receptor.partyType !== 'BusinessProcess')
        .map(participant => {
          if (!participant.id) {
            return {
              id: this.loggedInAgent.id,
              name: this.loggedInAgent.name,
              partyType: 'Agent'
            };
          } else {
            return participant;
          }
        });
    },

    hasEnded() {
      return this.element.isEnded || this.interaction.isHistoric || this.interaction.isOnWrapUp;
    },

    endedTimeAgo() {
      if (!this.interaction.timing.endTime) {
        return moment.utc(this.interaction.interactionStateTime).fromNow();
      }
      return moment.utc(this.interaction.timing.endTime).fromNow();
    },

    calculatedDuration() {
      if (!this.interaction.timing.startTime || !this.interaction.timing.endTime) {
        return null;
      }

      const startTime = moment.utc(this.interaction.timing.startTime);
      const endTime = moment.utc(this.interaction.timing.endTime);
      const diff = moment.utc(endTime.diff(startTime));

      return EcsKpiFormatter.methods.kpiFormat(diff.format('HH:mm:ss'), 'duration', true);
    },

    talkTime() {
      return EcsKpiFormatter.methods.kpiFormat(this.interaction.timing.talkTime, 'duration', true);
    },

    waitTime() {
      return EcsKpiFormatter.methods.kpiFormat(this.interaction.timing.waitTime, 'duration', true);
    },

    handlingDuration() {
      return EcsKpiFormatter.methods.kpiFormat(this.interaction.timing.handlingDuration, 'duration', true);
    },

    callDurationFormatted() {
      return moment.utc(this.callDuration).format('HH:mm:ss');
    },

    options() {
      if (this.interaction.isDirectCall) {
        return this.getCallControlOptions(!!this.directCallInteractionIdOnHold);
      }

      const { isOnHold, isEnded, recording, isConferenced } = this.element;

      const callControlOptions = this.getCallControlOptions(isOnHold, isEnded, isConferenced);

      // Call Recording (USES UNSHIFT - ORDER MATTERS)
      // Depends on the recording property of the element that was
      // created based on the recording option of the logged in agent
      if (recording && this.loggedInAgent.callRecording) {
        const recordingOption = this.loggedInAgent.callRecording.recordingOption.toUpperCase();
        const { isEnabled, isPaused } = recording;

        if (recordingOption === 'ALWAYS WITH PAUSE/RESUME') {
          callControlOptions.unshift({
            icon: 'mdi-play-pause',
            label: isPaused ? 'options.resumeRecording' : 'options.pauseRecording',
            action: isPaused ? this.resumeRecording : this.pauseRecording,
            waitsFor: isPaused ? ['INTERACTION_RECORDING_RESUMED_EVENT'] : ['INTERACTION_RECORDING_PAUSED_EVENT'],
            active: isPaused,
            disabled: isEnded
          });
        } else if (recordingOption.includes('ON DEMAND')) {
          this.addOnDemandCallControlOptions(callControlOptions, isEnabled, isPaused, isEnded, recordingOption);
        }
      }

      return callControlOptions;
    }
  },

  sockets: {
    INTERACTION_CONFERENCE_STOPPED_EVENT(interaction) {
      if (interaction.interactionId === this.element.interactionId) {
        this.startOnHoldCounter();
      }
    }
  },

  watch: {
    element: {
      handler(updatedElement, oldElement) {
        if (updatedElement.isOnHold && oldElement.isOnHold) {
          return;
        }
        if (updatedElement.isEnded) {
          clearInterval(this.callDurationTimer);
        }
        if (updatedElement.isOnHold) {
          this.startOnHoldCounter();
        } else {
          clearInterval(this.onHoldCounterInterval);
          sessionStorage.removeItem(`onHoldSince-${this.element.elementId}`);
        }
      },
      deep: true
    }
  },

  mounted() {
    if (this.interaction.isHistoric) {
      clearInterval(this.callDurationTimer);
    } else {
      this.startCallDurationTimer();
    }

    if (this.element.isOnHold) {
      this.startOnHoldCounter(true);
    }

    this.$root.$on(`agent-actions-toggled-${this.element.interactionId}`, this.handleAgentActionsToggle);
  },

  beforeDestroy() {
    if (this.element) {
      this.$root.$off(`agent-actions-toggled-${this.element.interactionId}`, this.handleAgentActionsToggle);
    }

    if (this.callDurationTimer) {
      clearInterval(this.callDurationTimer);
    }

    clearInterval(this.onHoldCounterInterval);
  },

  methods: {
    ...mapActions('EVENT_TIMING', ['addEventTimingConstraint', 'clearEventTimingConstraint']),

    startOnHoldCounter(checkSessionStorage = false) {
      if (checkSessionStorage) {
        this.onHoldSince = moment.utc(sessionStorage.getItem(`onHoldSince-${this.element.elementId}`));
      } else {
        this.onHoldSince = moment.utc();
        sessionStorage.setItem(`onHoldSince-${this.element.elementId}`, this.onHoldSince.toISOString());
      }

      const getOnHoldDuration = () => moment.utc(moment.utc().diff(this.onHoldSince)).format('HH:mm:ss');

      this.onHoldFormatted = getOnHoldDuration();
      this.onHoldCounterInterval = setInterval(() => {
        this.onHoldFormatted = getOnHoldDuration();
      }, 1000);
    },

    isAgent(participant) {
      return [CONTACT_TYPE.agent, CONTACT_TYPE.supervisor, CONTACT_TYPE.enterprise, 'Agent'].includes(
        participant.type || participant.contactType || participant.partyType
      );
    },

    addOnDemandCallControlOptions(callControlOptions, isEnabled, isPaused, isEnded, recordingOption) {
      // Add Pause/Resume
      callControlOptions.unshift({
        icon: 'mdi-play-pause',
        label: isPaused ? 'options.resumeRecording' : 'options.pauseRecording',
        action: isPaused ? this.resumeRecording : this.pauseRecording,
        waitsFor: isPaused ? ['INTERACTION_RECORDING_RESUMED_EVENT'] : ['INTERACTION_RECORDING_PAUSED_EVENT'],
        active: isPaused,
        disabled: !isEnabled || isEnded
      });

      if (recordingOption === 'ON DEMAND WITH USER INITIATED START') {
        // Add Stop/Start
        callControlOptions.unshift({
          icon: 'mdi-record',
          label: isEnabled ? 'options.stopRecordingCall' : 'options.recordCall',
          action: isEnabled ? this.stopCallRecording : this.startCallRecording,
          waitsFor: isEnabled
            ? ['INTERACTION_RECORDING_BY_AGENT_STOPPED_EVENT']
            : ['INTERACTION_RECORDING_BY_AGENT_STARTED_EVENT'],
          active: isEnabled,
          disabled: isEnded
        });
      } else {
        // Add Start only
        callControlOptions.unshift({
          icon: 'mdi-record',
          label: 'options.recordCall',
          action: this.startCallRecording,
          waitsFor: ['INTERACTION_RECORDING_BY_AGENT_STARTED_EVENT'],
          active: isEnabled,
          disabled: isEnded
        });
      }
    },

    getCallControlOptions(isOnHold, isEnded, isConferenced) {
      // TODO: Pending to be enabled when ECS will provide a working endpoint
      // TODO: The following options are placeholder and are hidden until we got support from ECS
      const isVideoEnabled = false;
      const isSharingScreen = false;
      const isFullScreenMode = false;
      const isOnHoldWaitsFor = isConferenced ? [ELEMENT_STATE.conferenced] : [ELEMENT_STATE.active];
      const isOnHoldWaitsForFallback = isConferenced ? [ELEMENT_STATE.conferencedDiverted] : [ELEMENT_STATE.diverted];
      return [
        {
          active: isVideoEnabled,
          disabled: true,
          icon: isVideoEnabled ? 'mdi-video-off' : 'mdi-video',
          label: 'options.video',
          hidden: true
        },
        {
          action: this.isMuted ? this.unmute : this.mute,
          active: this.isMuted,
          disabled: !this.useBrowserToAnswer,
          icon: this.isMuted ? 'mdi-microphone-off' : 'mdi-microphone',
          label: this.isMuted ? 'options.micOn' : 'options.micOff'
        },
        {
          active: isSharingScreen,
          disabled: true,
          icon: isSharingScreen ? 'mdi-close-box-outline' : 'mdi-arrow-up-bold-box-outline',
          label: 'options.shareScreen',
          hidden: true
        },
        {
          active: isFullScreenMode,
          disabled: true,
          icon: isFullScreenMode ? 'mdi-fullscreen-exit' : 'mdi-fullscreen',
          label: 'options.fullScreen',
          hidden: true
        },
        {
          action: isOnHold ? this.resumeCall : this.holdCall,
          waitsFor: isOnHold ? isOnHoldWaitsFor : isOnHoldWaitsForFallback,
          active: isOnHold,
          disabled: isEnded,
          icon: 'mdi-phone-paused',
          label: isOnHold ? 'options.resumeCall' : 'options.holdCall'
        },
        {
          action: this.toggleDialpadVisibility,
          active: this.isDialpadVisible,
          disabled: isEnded,
          icon: 'mdi-dialpad',
          label: 'options.dialpad'
        }
      ];
    },

    isWaitingFor(events) {
      if (!events) {
        return null;
      }
      return this.isWaitingForEvent({
        interactionId: this.element.interactionId,
        constraintNames: events
      });
    },

    startCallDurationTimer() {
      this.callDurationTimer = setInterval(() => {
        if (this.interaction.isDirectCall) {
          this.callDuration = moment.utc().diff(moment.utc(this.interaction.timing.directCallAnsweredAt));
        } else {
          this.callDuration = moment.utc().diff(moment.utc(this.element.answeredAt));
        }
      }, 1000);
    },

    toggleDialpadVisibility() {
      this.isDialpadVisible = !this.isDialpadVisible;
    },

    handleDialpadDigitPressed(digit) {
      if (this.isSoftPhoneReadyToUse) {
        return this.dtmfDirectCallInteraction(digit);
      }

      InteractionService.sendTelephonyDtmf(digit).catch(error => {
        console.error('Error sending DTMF', error);
      });
    },

    handleAgentActionsToggle() {
      this.handleDialpadClosed();
    },

    handleDialpadClosed() {
      this.isDialpadVisible = false;
    },

    holdCall() {
      if (this.interaction.isDirectCall) {
        this.directCallInteractionIdOnHold = this.interaction.interactionId;
        return this.holdDirectCallInteraction();
      }
      this.addEventTimingConstraint({
        interactionId: this.element.interactionId,
        constraintNames: this.element.isConferenced ? [ELEMENT_STATE.conferencedDiverted] : [ELEMENT_STATE.diverted]
      });

      InteractionService.holdElement(this.element.interactionId, this.element.elementId).catch(() =>
        this.clearEventTimingConstraint(this.element)
      );
    },

    resumeCall() {
      if (this.interaction.isDirectCall) {
        this.directCallInteractionIdOnHold = '';
        return this.resumeDirectCallInteraction();
      }
      this.addEventTimingConstraint({
        interactionId: this.element.interactionId,
        constraintNames: this.element.isConferenced ? [ELEMENT_STATE.conferenced] : [ELEMENT_STATE.active]
      });

      InteractionService.resumeElement(this.element.interactionId, this.element.elementId).catch(() =>
        this.clearEventTimingConstraint(this.element)
      );
    },

    mute() {
      if (this.isSoftPhoneFeatureEnabledAndReadyToUse) {
        this.isMuted = true;
        this.$root.$emit('soft-phone-action', { action: ACTIONS.mute });
      }
    },

    unmute() {
      if (this.isSoftPhoneFeatureEnabledAndReadyToUse) {
        this.isMuted = false;
        this.$root.$emit('soft-phone-action', { action: ACTIONS.unmute });
      }
    },

    startCallRecording() {
      const recordingState = this.getCallRecordingStateByInteractionId(this.element.interactionId);

      if (recordingState && recordingState.isRecording) {
        return this.$root.$emit('snackbar-message', {
          message: this.$t('alreadyRecording'),
          type: 'info'
        });
      }

      this.addEventTimingConstraint({
        interactionId: this.element.interactionId,
        constraintNames: ['INTERACTION_RECORDING_BY_AGENT_STARTED_EVENT']
      });

      InteractionService.startCallRecording(this.element.interactionId).catch(() =>
        this.clearEventTimingConstraint(this.element)
      );
    },

    pauseRecording() {
      this.addEventTimingConstraint({
        interactionId: this.element.interactionId,
        constraintNames: ['INTERACTION_RECORDING_PAUSED_EVENT']
      });

      InteractionService.pauseCallRecording(this.element.interactionId).catch(() =>
        this.clearEventTimingConstraint(this.element)
      );
    },

    resumeRecording() {
      this.addEventTimingConstraint({
        interactionId: this.element.interactionId,
        constraintNames: ['INTERACTION_RECORDING_RESUMED_EVENT']
      });

      InteractionService.resumeCallRecording(this.element.interactionId).catch(() =>
        this.clearEventTimingConstraint(this.element)
      );
    },

    stopCallRecording() {
      this.addEventTimingConstraint({
        interactionId: this.element.interactionId,
        constraintNames: ['INTERACTION_RECORDING_BY_AGENT_STOPPED_EVENT']
      });

      InteractionService.stopCallRecording(this.element.interactionId).catch(() =>
        this.clearEventTimingConstraint(this.element)
      );
    }
  }
};
</script>
<style>
.call-widget-container {
  height: 100%;
  position: relative;
  background-color: var(--v-surface-darken1);
}

.call-widget-avatar {
  position: relative;
  height: calc(100% - 20px);
  list-style: none;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 0 60px 0;
}

.call-widget-avatar .v-avatar {
  margin: auto;
}

.call-widget-avatar .v-avatar .v-icon {
  font-size: 64px;
  color: #fff;
}

.call-widget-avatar .conference-avatar {
  margin: 8px;
}

.call-widget {
  position: absolute;
  bottom: 24px;
  left: 0;
  right: 0;
  width: -moz-max-content;
  width: max-content;
  margin: auto;
  background-color: var(--v-surface-lighten1);
  border-radius: 4px;
}

.call-widget-message {
  position: absolute;
  bottom: 28px;
  left: 0;
  right: 0;
  width: max-content;
  margin: auto;
  opacity: 0.75;
  background-color: var(--v-surface-lighten1);
  border-radius: 4px;
  padding: 8px 16px;
  max-width: calc(100% - 120px);
  display: grid;
  grid-template-columns: 24px auto;
  align-items: center;
}

.call-widget-message .message {
  padding-left: 8px;
  color: #fff9;
}

.call-widget-message .message span {
  color: #fff;
  font-weight: bold;
}

.call-widget-message:not([dark]) .message {
  color: #444;
}

.call-widget-message:not([dark]) .message span {
  color: #222;
}

.call-widget-controls {
  display: flex;
  height: 40px;
  align-items: center;
  padding: 0 16px;
}

.customer-avatar,
.consulted-avatar {
  display: flex;
  flex: 1;
  height: 100%;
  align-items: center;
  justify-content: center;
}

.consulted-avatar {
  background-color: var(--v-surface-darken1);
}

.control {
  border-radius: 0;
  height: 40px !important;
  width: 36px !important;
  margin-right: 8px;
}

.call-widget-avatar .avatar-initials {
  border-color: inherit;
}

.ecs-telephony-element-dialpad .ecs-dialpad-overlay {
  z-index: 48;
}

.ecs-telephony-element-dialpad .ecs-dialpad {
  z-index: 49;
}

.on-hold-counter {
  font-size: 12px;
  opacity: 0.75;
  text-align: center;
  border-bottom: 1px solid var(--v-border-color);
  background: var(--v-info-base);
  overflow: hidden;
  border-radius: 4px 4px 0 0;
  color: white;
}
</style>
